import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FetchCallbacksContext } from '../../FetchCallbacks';
import fetchAPI from '../../../utils/fetchAPI';
import FormBuilderWorldcheckView from '../view/FormBuilderWorldcheckView';
import getApiEndpoints, {
  EndpointTypes
} from '../../../components/form/FormEndpoints';
import useTranslations from '../../../hooks/useTranslations';
import { LOAD_FORM } from '../../../store/modules/state/formBuilder';
import useGlobalVariables from '../../../hooks/useGlobalVariables';

const TAB_VALUES = {
  AUTHENTICATION: 0,
  SETTINGS: 1
};

const FormBuilderWorldcheckEngine = ({
  formId,
  integrationType,
  integrationData: receivedIntegrationData,
  dispatch
}) => {
  const t = useTranslations();
  const { setIsLoading } = useGlobalVariables();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const apiEndpoints = useMemo(() => getApiEndpoints(EndpointTypes.ADMIN), []);
  const [activeTab, setActiveTab] = useState(TAB_VALUES.AUTHENTICATION);
  const [worldcheckConnected, setWorldcheckConnected] = useState(
    receivedIntegrationData && receivedIntegrationData.id !== 'new'
  );
  const [watchlistType, setWatchlistType] = useState('INITIAL');
  const [isAutoResolve, setIsAutoResolve] = useState(false);
  const [standardIndividual, setStandardIndividual] = useState(false);
  const [secret, setSecret] = useState('');
  const [groupId, setGroupId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [negativeStatusId, setNegativeStatusId] = useState('');
  const [riskOptions, setRiskOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [autoResolutionRisk, setAutoResolutionRisk] = useState('');
  const [autoResolutionReason, setAutoResolutionReason] = useState('');
  const [autoResolutionComment, setAutoResolutionComment] = useState('');

  const switchChangedManually = useRef(false);

  const [integrationData, setIntegrationData] = useState(
    receivedIntegrationData
  );

  const getCurrentInfo = () => {
    setIsLoading(true);
    fetchAPI(
      `/scheduler/v1/integration/${integrationData.id}/serverdetails`
    ).then(res => {
      let isAutoResolve;
      if (!res || !res.integrationServerDetails) {
        setIsLoading(false);
        return;
      }
      isAutoResolve =
        res.integration?.additionalParams?.autoResolveConfiguration;

      setIsAutoResolve(isAutoResolve);
      setWatchlistType(
        res.integration?.additionalParams?.ongoingCheckOptions?.WATCHLIST ||
          'INITIAL'
      );
      setSecret(res.integrationServerDetails.secret || '');
      setGroupId(res.integrationServerDetails.groupId || '');
      setAccountId(res.integrationServerDetails.accountId || '');
      setIntegrationData(res);
      getResolutionToolkit(integrationData.id)
        .then(resolutionData => {
          const { statuses, risks, reasons } =
            resolutionData?.resolutionFields || {};
          const falseStatus = statuses.find(one => one.type === 'FALSE');
          const falseStatusId = falseStatus.id;

          let newRiskOptions = risks.map(one => {
            return {
              text: one.label,
              value: one.id
            };
          });
          let newReasonOptions = reasons.map(one => {
            return {
              text: one.label,
              value: one.id
            };
          });
          if (
            resolutionData?.resolutionRules &&
            resolutionData.resolutionRules[falseStatusId]
          ) {
            let resolutionRulesForFalse =
              resolutionData?.resolutionRules[falseStatusId];
            if (resolutionRulesForFalse?.risks) {
              newRiskOptions = newRiskOptions.filter(one =>
                resolutionRulesForFalse.risks.includes(one.value)
              );
            }
            if (resolutionRulesForFalse?.reasons) {
              newReasonOptions = newReasonOptions.filter(one =>
                resolutionRulesForFalse.reasons.includes(one.value)
              );
            }
          }
          setNegativeStatusId(falseStatus.id);
          setRiskOptions(newRiskOptions);
          setReasonOptions(newReasonOptions);
          if (res.integration?.additionalParams?.autoResolveConfiguration) {
            const autoResolveObject =
              res.integration.additionalParams.autoResolveConfiguration;
            setAutoResolutionComment(autoResolveObject.resolutionRemark || '');
            setAutoResolutionReason(autoResolveObject.reasonId || '');
            setAutoResolutionRisk(autoResolveObject.riskId || '');
          }
          setIsLoading(false);
        })
        .catch(e => {
          openSnackbar(
            'error',
            t(`snackbars.integrations.${integrationType}_toolkit_failure`)
          );
          setIsLoading(false);
        });
    });
  };

  const getResolutionToolkit = integrationId => {
    return new Promise((resolve, reject) => {
      fetchAPI(
        `/scheduler/v1/wc/results/integration/${integrationData.id}/resolution/toolkit`
      )
        .then(res => {
          resolve(res);
        })
        .catch(e => {
          reject('case not found');
        });
    });
  };

  useEffect(() => {
    if (integrationData && integrationData.id && integrationData.id !== 'new') {
      getCurrentInfo();
    }
  }, [integrationData]);

  useEffect(() => {
    if (!switchChangedManually.current) {
      return;
    }
    switchChangedManually.current = false;
    saveChanges();
  }, [switchChangedManually, watchlistType]);

  useEffect(() => {
    if (activeTab === TAB_VALUES.SETTINGS) {
      if (!isAutoResolve) {
        saveChanges();
      } else if (
        autoResolutionRisk &&
        autoResolutionReason &&
        autoResolutionComment
      ) {
        saveChanges();
      }
    }
  }, [isAutoResolve]);

  useEffect(() => {
    if (
      activeTab !== TAB_VALUES.SETTINGS ||
      !autoResolutionRisk ||
      !autoResolutionReason ||
      !autoResolutionComment
    ) {
      return;
    }
    saveChanges();
  }, [autoResolutionRisk, autoResolutionReason]);

  const onBlurCommentField = () => {
    if (autoResolutionRisk && autoResolutionReason && autoResolutionComment) {
      saveChanges();
    }
  };

  const resetFormToDefaults = () => {
    setWorldcheckConnected(false);
    setStandardIndividual(false);
    setSecret('');
    setGroupId('');
    setAccountId('');
    setExistingFiles([]);
  };

  const onConnectBtnClicked = useCallback(() => {
    fetchAPI(`/scheduler/v1/integration/connect/${integrationType}`, {
      method: 'PUT',
      body: {
        ftp_details: {
          secret: secret,
          groupId: groupId,
          accountId: accountId
        },
        integration: {
          integrationType: integrationType,
          assetId: formId,
          additionalParams: {
            ongoingCheckOptions: {
              WATCHLIST: watchlistType
            }
          }
        }
      },
      handleResponse: r => {
        const newIntegrationId = r.headers.map.location;
        setIntegrationData({ id: newIntegrationId });
        setWorldcheckConnected(true);
        openSnackbar(
          'success',
          `snackbars.integrations.${integrationType}_success`
        );
      }
    }).catch(() => {
      openSnackbar(
        'error',
        `snackbars.integrations.${integrationType}_failure`
      );
    });
  }, [secret, formId, groupId, accountId]);

  const onDisconnectBtnClicked = useCallback(() => {
    setIsDisconnectDialogOpen(false);
    if (!integrationData?.integration?.id || integrationData.id === 'new') {
      return;
    }
    fetchAPI(
      `/forms/v1/forms/${formId}/integration/${integrationData.integration.id}/deactivate?integrationType=${integrationType}`,
      {
        method: 'DELETE'
      }
    )
      .then(res => {
        fetchAPI(`/forms/v1/forms/${formId}`).then(r => {
          dispatch({ type: LOAD_FORM, payload: { ...r, id: formId } });
          resetFormToDefaults();
          openSnackbar(
            'success',
            `snackbars.integrations.${integrationType}_disconnect_success`
          );
        });
      })
      .catch(() => {
        openSnackbar(
          'error',
          `snackbars.integrations.${integrationType}_disconnect_failure`
        );
      });
  }, [apiEndpoints, integrationData, secret]);

  const saveChanges = useCallback(() => {
    if (!secret || !groupId || !accountId || !worldcheckConnected) {
      openSnackbar(
        'error',
        `snackbars.integrations.${integrationType}_out_of_order`
      );
      return;
    }
    let newIntegrationData = { ...integrationData };
    if (!newIntegrationData.integration) {
      newIntegrationData.integration = {};
    }
    if (!newIntegrationData.integration.additionalParams) {
      newIntegrationData.integration.additionalParams = {};
    }
    if (!newIntegrationData.integration.additionalParams.ongoingCheckOptions) {
      newIntegrationData.integration.additionalParams.ongoingCheckOptions = {};
    }
    newIntegrationData.integration.additionalParams.ongoingCheckOptions.WATCHLIST =
      watchlistType;

    if (isAutoResolve) {
      newIntegrationData.integration.additionalParams.autoResolveConfiguration =
        {
          riskId: autoResolutionRisk,
          reasonId: autoResolutionReason,
          statusId: negativeStatusId,
          resolutionRemark: autoResolutionComment
        };
    } else {
      delete newIntegrationData.integration.additionalParams
        .autoResolveConfiguration;
      delete newIntegrationData.integration.additionalParams.isAutoResolve;
      setAutoResolutionComment('');
      setAutoResolutionReason('');
      setAutoResolutionRisk('');
    }

    // rename integrationServerDetails to ftp_details
    newIntegrationData.ftp_details =
      newIntegrationData.integrationServerDetails || {};
    delete newIntegrationData.integrationServerDetails;

    //update the three main fields if necessary
    newIntegrationData.ftp_details.secret = secret;
    newIntegrationData.ftp_details.groupId = groupId;
    newIntegrationData.ftp_details.accountId = accountId;

    fetchAPI(`/scheduler/v1/integration/connect/${integrationType}`, {
      method: 'PUT',
      body: newIntegrationData,
      handleResponse: r => {
        openSnackbar(
          'success',
          `snackbars.integrations.${integrationType}_save_success`
        );
      }
    }).catch(() => {
      openSnackbar(
        'error',
        `snackbars.integrations.${integrationType}_save_failure`
      );
    });
  }, [
    secret,
    groupId,
    accountId,
    worldcheckConnected,
    watchlistType,
    isAutoResolve,
    integrationData,
    autoResolutionRisk,
    autoResolutionReason,
    autoResolutionComment
  ]);

  const onToggleSwitch = useCallback(
    e => {
      if (!secret || !groupId || !accountId || !worldcheckConnected) {
        openSnackbar(
          'error',
          `snackbars.integrations.${integrationType}_out_of_order`
        );
        return;
      }

      const checked = e.target.checked;
      const newValue = checked ? 'ONGOING' : 'INITIAL';
      switchChangedManually.current = true;
      setWatchlistType(newValue);
    },
    [secret, groupId, accountId, worldcheckConnected, integrationData]
  );

  const onToggleAutoResolve = useCallback(
    e => {
      if (!secret || !groupId || !accountId || !worldcheckConnected) {
        openSnackbar(
          'error',
          `snackbars.integrations.${integrationType}_out_of_order`
        );
        return;
      }

      const checked = e.target.checked;
      switchChangedManually.current = true;
      setIsAutoResolve(checked);
    },
    [secret, groupId, accountId, worldcheckConnected, integrationData]
  );

  const onSaveBtnClicked = useCallback(() => {
    saveChanges();
  }, [
    secret,
    groupId,
    accountId,
    worldcheckConnected,
    watchlistType,
    isAutoResolve,
    integrationData,
    autoResolutionRisk,
    autoResolutionReason,
    autoResolutionComment
  ]);

  const viewProps = {
    activeTab,
    watchlistType,
    isAutoResolve,
    riskOptions,
    reasonOptions,
    autoResolutionComment,
    autoResolutionReason,
    autoResolutionRisk,
    onBlurCommentField,
    onToggleSwitch,
    onToggleAutoResolve,
    setAutoResolutionComment,
    setAutoResolutionReason,
    setAutoResolutionRisk,
    setActiveTab,
    TAB_VALUES,
    existingFiles,
    secret,
    setSecret,
    groupId,
    setGroupId,
    accountId,
    setAccountId,
    worldcheckConnected,
    standardIndividual,
    setStandardIndividual,
    onConnectBtnClicked,
    onSaveBtnClicked,
    onDisconnectBtnClicked,
    isDisconnectDialogOpen,
    setIsDisconnectDialogOpen,
    integrationType
  };

  return <FormBuilderWorldcheckView {...viewProps} />;
};

export default FormBuilderWorldcheckEngine;
